import React, { useState, useEffect } from 'react';

import OrganigramCanvasPlaceholder from 'Components/workforce/OrganigramCanvasPlaceholder';
import UnassignedPositionsPanel from 'Components/workforce/UnassignedPositionsPanel';
import WorkforceTypeFilterPanel from 'Components/workforce/WorkforceTypeFilterPanel';

import useOrganigramFilter from 'Hooks/useOrganigramFilter';
import useUnassignedPanel from 'Hooks/useUnassignedPanel';

import OrgChart from 'Scripts/template';

export default function VenueOrganigram(props) {
    // null until the tab is opened and the tree loads. An EMPTY array is a
    // loaded result, not an absent one — with the panel excluding unassigned
    // positions a venue can legitimately have an empty canvas, and the panel
    // beside it is the only way to fill it.
    const [organigram, setOrganigram] = useState(null);
    const [failed, setFailed] = useState(false);

    const preferenceKey = `venue_${props.eventId}`;
    const { filterState, setFilterState, filteredNodes } = useOrganigramFilter({
        initialFilterState: props.initialFilterState ? props.initialFilterState[preferenceKey] : undefined,
        fullPreferenceValue: props.fullPreferenceValue ?? {},
        preferenceKey,
        nodes: organigram || [],
    });

    // A venue's reporting line is its own — `position_venue.supervisor_id`, not
    // the org chart's — so the panel, the exclusion and the drop all speak to
    // the venue endpoints.
    const panel = useUnassignedPanel({
        unassignedUrl: props.unassignedUrl,
        treeUpdateUrl: props.treeUpdateUrl,
        onAssigned: () => fetchData(),
    });

    useEffect(() => {
        if (props.organisationChartTabActive) {
            fetchData();
        }
    }, [props.organisationChartTabActive]);

    const fetchData = () => {
        setFailed(false);
        axios
            .get(props.fetchUrl, { params: panel.treeParams })
            .then((res) => {
                setOrganigram(res.data);
            })
            .catch(() => {
                setFailed(true);
            });
    };

    // Only the canvas waits. The heading, the key and the panel are drawn from
    // the first paint, so a large venue does not open on a blank page — and the
    // key is readable while the chart it explains is still arriving.
    const stillLoading = organigram === null && !failed;

    return (
        <div className="tw-space-y-4 tw-flex tw-flex-col tw-h-full">
            <div className="tw-flex tw-items-center tw-gap-x-3">
                <div className="tw-flex-grow">
                    <h5 className="text-blue font-weight-600 mb-0">{$t('Venue Organigram')}</h5>
                </div>
            </div>
            <WorkforceTypeFilterPanel filterState={filterState} onChange={setFilterState} />
            {/* The panel is positioned rather than laid out beside the chart: as a
                flex sibling its rows would set the row height and stretch the canvas. */}
            <div className="tw-relative tw-flex tw-h-full tw-min-h-0">
                <div className="tw-h-full tw-w-full tw-min-w-0" style={{ paddingInlineEnd: panel.gutter }}>
                    {stillLoading ? (
                        <OrganigramCanvasPlaceholder />
                    ) : failed ? (
                        <OrganigramCanvasPlaceholder message={$t('The chart could not be loaded. Refresh the page to try again.')} />
                    ) : (
                        <OrgChart
                            nodes={filteredNodes}
                            treeUpdateUrl={props.treeUpdateUrl}
                            exportContext={props.exportContext}
                            includePercentage
                            hideActions
                            includeBudgetedStaff
                            {...panel.chartProps}
                        />
                    )}
                </div>
                {panel.enabled && <UnassignedPositionsPanel {...panel.panelProps} />}
            </div>
        </div>
    );
}
